import React from "react";
import { motion } from "framer-motion";
import cheeto from "../assets/why.png"; 

const Why = () => {
  // Animation variants
  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const iconAnimation = {
    hidden: { scale: 0, rotate: -90 },
    visible: { 
      scale: 1, 
      rotate: 0,
      transition: { 
        type: "spring", 
        stiffness: 200,
        damping: 15 
      }
    }
  };

  const imageAnimation = {
    hidden: { opacity: 0, scale: 0.8, rotateY: 180 },
    visible: { 
      opacity: 1, 
      scale: 1,
      rotateY: 0,
      transition: { 
        duration: 1,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="bg-gradient-to-b from-white to-[#f6fbff] py-16">
      <div className="container mx-auto px-6 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Content */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={fadeInLeft}
        >
          <motion.h2 
            className="text-3xl md:text-4xl font-extrabold text-[#0b1b33] leading-snug mb-4"
            variants={fadeInUp}
          >
            Why Gentle Matters: Understanding <br /> Toothpaste Ingredients
          </motion.h2>

          <motion.p 
            className="text-gray-600 leading-relaxed mb-8"
            variants={fadeInUp}
          >
            Many conventional toothpastes contain harsh chemicals like SLS (Sodium Lauryl Sulfate)
            that can irritate young mouths. We've formulated <span className="font-semibold">SRIDANTH</span> to be
            completely SLS-free, ensuring a gentle yet effective clean that protects developing teeth
            and sensitive gums.
          </motion.p>

          {/* Info Boxes */}
          <motion.div 
            className="space-y-4"
            variants={staggerContainer}
          >
            {/* Card 1 */}
            <motion.div
              variants={fadeInUp}
              whileHover={{ 
                x: 10,
                transition: { type: "spring", stiffness: 400, damping: 25 }
              }}
              className="bg-white shadow-sm rounded-xl p-4 flex items-start gap-3 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-default group"
            >
              <motion.div 
                className="flex-shrink-0 mt-1"
                variants={iconAnimation}
                whileHover={{ scale: 1.2, rotate: 360 }}
                transition={{ duration: 0.3 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2l4-4m5 2a9 9 0 11-18 0a9 9 0 0118 0z"
                  />
                </svg>
              </motion.div>
              <div>
                <motion.h3 
                  className="font-semibold text-[#0b1b33] group-hover:text-green-600 transition-colors duration-300"
                  whileHover={{ scale: 1.02 }}
                >
                  Why Choose SLS-Free?
                </motion.h3>
                <motion.p 
                  className="text-gray-500 text-sm"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  Gentle on sensitive mouths without compromising effectiveness
                </motion.p>
              </div>
            </motion.div>

            {/* Card 2 */}
            <motion.div
              variants={fadeInUp}
              whileHover={{ 
                x: 10,
                transition: { type: "spring", stiffness: 400, damping: 25 }
              }}
              className="bg-white shadow-sm rounded-xl p-4 flex items-start gap-3 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-default group"
            >
              <motion.div 
                className="flex-shrink-0 mt-1"
                variants={iconAnimation}
                whileHover={{ scale: 1.2, rotate: 360 }}
                transition={{ duration: 0.3 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2l4-4m5 2a9 9 0 11-18 0a9 9 0 0118 0z"
                  />
                </svg>
              </motion.div>
              <div>
                <motion.h3 
                  className="font-semibold text-[#0b1b33] group-hover:text-green-600 transition-colors duration-300"
                  whileHover={{ scale: 1.02 }}
                >
                  Gentle Oral Health
                </motion.h3>
                <motion.p 
                  className="text-gray-500 text-sm"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  Protects enamel while fighting cavities naturally
                </motion.p>
              </div>
            </motion.div>

            {/* Card 3 */}
            <motion.div
              variants={fadeInUp}
              whileHover={{ 
                x: 10,
                transition: { type: "spring", stiffness: 400, damping: 25 }
              }}
              className="bg-white shadow-sm rounded-xl p-4 flex items-start gap-3 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-default group"
            >
              <motion.div 
                className="flex-shrink-0 mt-1"
                variants={iconAnimation}
                whileHover={{ scale: 1.2, rotate: 360 }}
                transition={{ duration: 0.3 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2l4-4m5 2a9 9 0 11-18 0a9 9 0 0118 0z"
                  />
                </svg>
              </motion.div>
              <div>
                <motion.h3 
                  className="font-semibold text-[#0b1b33] group-hover:text-green-600 transition-colors duration-300"
                  whileHover={{ scale: 1.02 }}
                >
                  A Smarter Choice
                </motion.h3>
                <motion.p 
                  className="text-gray-500 text-sm"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  Science-backed formula parents can trust
                </motion.p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Right Image */}
        <motion.div 
          className="flex justify-center lg:justify-end"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={fadeInRight}
        >
          <motion.div 
            className="bg-white shadow-lg rounded-3xl p-6 flex justify-center items-center"
            variants={imageAnimation}
            whileHover={{ 
              scale: 1.05,
              rotateY: 5,
              transition: { type: "spring", stiffness: 300 }
            }}
          >
            <motion.img
              src={cheeto}
              alt="SRIDANTH Cheeto Toothpaste"
              className="w-[90%] max-w-md object-contain"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Why;